import { useSyncExternalStore } from 'react';
import axios from 'axios';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, CartItem, CheckoutRequest, CheckoutResponse } from '@/services/types';
import { API_ENDPOINTS, STORAGE_KEYS } from '@/utils/constants';
import { loadHeldCart, setOrderType, setTotalDiscount, OrderType } from '@/features/cart/cartSlice';
import type { AppDispatch } from '@/app/store';
import toast from 'react-hot-toast';
import i18n from '@/i18n/config';

/**
 * Offline / fire-and-forget queue for rwallet (EPAYMENT) sales.
 * Persisted in localStorage, posted serially with exponential backoff (1s..30s), resumed on load.
 * Safe to retry because every request carries its own idempotencyKey.
 *   error == null  → pending (still retrying)
 *   error != null  → server rejected (4xx); terminal, user restores to cart or discards.
 */
export interface QueuedCart {
    items: CartItem[];
    customerName: string;
    customerPhone: string;
    notes: string;
    orderType: OrderType;
    totalDiscount: number;
}

export interface QueuedSale {
    id: string;
    shopId: number;
    request: CheckoutRequest;
    cart: QueuedCart;
    error: string | null;
    queuedAt: number;
}

let queue: QueuedSale[] = load();
let running = false;
let started = false;
let attempt = 0;
const listeners = new Set<() => void>();

function load(): QueuedSale[] {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEYS.CHECKOUT_QUEUE) || '[]');
    } catch {
        return [];
    }
}

function save(next: QueuedSale[]) {
    queue = next;
    localStorage.setItem(STORAGE_KEYS.CHECKOUT_QUEUE, JSON.stringify(queue));
    listeners.forEach((l) => l());
}

export const getQueue = () => queue;
export const hasPendingSales = () => queue.some((s) => s.error === null);
export const subscribeQueue = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
export const useCheckoutQueue = () => useSyncExternalStore(subscribeQueue, getQueue, () => queue);

export function enqueueSale(sale: Omit<QueuedSale, 'error' | 'queuedAt'>) {
    save([...queue, { ...sale, error: null, queuedAt: Date.now() }]);
    void process();
}

export function discardSale(id: string) {
    save(queue.filter((s) => s.id !== id));
}

/** Put a failed sale back into the active cart. Caller must ensure the cart is empty. */
export function restoreToCart(id: string, dispatch: AppDispatch) {
    const sale = queue.find((s) => s.id === id);
    if (!sale) return;
    dispatch(loadHeldCart({
        items: sale.cart.items,
        customerName: sale.cart.customerName,
        customerPhone: sale.cart.customerPhone,
        notes: sale.cart.notes,
    }));
    dispatch(setOrderType(sale.cart.orderType));
    dispatch(setTotalDiscount(sale.cart.totalDiscount));
    discardSale(id);
}

export function startCheckoutQueue() {
    if (started) return;
    started = true;
    window.addEventListener('online', () => { attempt = 0; void process(); });
    void process();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function process(): Promise<void> {
    if (running) return;
    running = true;
    try {
        for (;;) {
            const sale = queue.find((s) => s.error === null);
            if (!sale) break;
            try {
                const res = await api.post<ApiResponse<CheckoutResponse>>(
                    API_ENDPOINTS.CHECKOUT(sale.shopId),
                    sale.request
                );
                attempt = 0;
                save(queue.filter((s) => s.id !== sale.id));
                toast.success(i18n.t('queue.saleSynced', { receipt: res.data.data.receiptNumber }));
            } catch (err) {
                const status = axios.isAxiosError(err) ? err.response?.status ?? -1 : -1;
                const transient = status === -1 || status >= 500;
                if (!transient) {
                    save(queue.map((s) => (s.id === sale.id ? { ...s, error: getErrorMessage(err) } : s)));
                    continue;
                }
                // ponytail: timer-only backoff (plus 'online' event); no connectivity-aware scheduling
                await sleep(Math.min(1 << attempt, 30) * 1000);
                attempt = Math.min(attempt + 1, 5);
            }
        }
    } finally {
        running = false;
    }
}
